import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

/**
 * Aerial perspective + volumetric sun shafts.
 *
 * Runs in scene-referred linear space (the composer target is HalfFloat and
 * OutputPass does the tone map at the end), so the fog colour is a radiance and
 * is allowed to exceed 1.0 near the sun. Fog density falls off exponentially
 * with height, which keeps the street readable while the rooftops and the far
 * end of the block wash into haze - the depth cue that a flat exponential fog
 * cannot give you without also greying out the midground.
 *
 * Depth comes from GTAOPass's normal/depth prepass, which is already rendered
 * every frame and is a render target nobody else writes into (so no feedback
 * loop with the composer ping-pong buffers).
 */
const AerialShader = {
  defines: { RAY_STEPS: 32 },
  uniforms: {
    tDiffuse: { value: null },
    tDepth: { value: null },
    tNormal: { value: null },
    uInvProjection: { value: new THREE.Matrix4() },
    uProjScale: { value: new THREE.Vector2(1, 1) },
    uContactRadius: { value: 0.22 },
    uContactStrength: { value: 0.85 },
    uCamWorld: { value: new THREE.Matrix4() },
    uCamPos: { value: new THREE.Vector3() },
    uNear: { value: 0.02 },
    uFar: { value: 600 },
    uAspect: { value: 1.777 },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uSunScreen: { value: new THREE.Vector3(0.5, 0.5, 0) },
    uSunColor: { value: new THREE.Color(1, 0.8, 0.55) },
    uFogColor: { value: new THREE.Color(0.26, 0.33, 0.45) },
    uFogSunColor: { value: new THREE.Color(0.95, 0.68, 0.45) },
    uFogDensity: { value: 0.0078 },
    uFogHeight: { value: 34.0 },
    uFogBase: { value: -2.0 },
    uFogMax: { value: 0.78 },
    uRayStrength: { value: 0.30 },
    uRayDecay: { value: 0.965 },
    uRaySpread: { value: 0.85 },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
  `,
  fragmentShader: /* glsl */`
    precision highp float;
    uniform sampler2D tDiffuse, tDepth, tNormal;
    uniform mat4 uInvProjection, uCamWorld;
    uniform vec2 uProjScale;
    uniform float uContactRadius, uContactStrength;
    uniform vec3 uCamPos, uSunDir, uSunScreen, uSunColor, uFogColor, uFogSunColor;
    uniform float uNear, uFar, uAspect, uFogDensity, uFogHeight, uFogBase, uFogMax;
    uniform float uRayStrength, uRayDecay, uRaySpread;
    varying vec2 vUv;

    float readDepth(vec2 uv) { return texture2D(tDepth, uv).x; }

    float linearDepth(float d) {
      float ndc = d * 2.0 - 1.0;
      return (2.0 * uNear * uFar) / (uFar + uNear - ndc * (uFar - uNear));
    }

    // View-space position of the fragment at a given uv + linear depth.
    vec3 viewPos(vec2 uv, float linZ) {
      vec4 clip = vec4(uv * 2.0 - 1.0, -1.0, 1.0);
      vec4 v = uInvProjection * clip;
      vec3 dir = v.xyz / v.w;
      return dir * (linZ / max(-dir.z, 1e-4));
    }

    // Eight fixed directions, alternating inner/outer ring. Fixed rather than
    // per-pixel rotated: at this radius a stochastic kernel is pure noise and
    // the grain pass would only amplify it. Generated rather than declared as a
    // const array because ShaderPass compiles as GLSL ES 1.00, which has no
    // array constructors.

    /**
     * Short-range contact occlusion. GTAO's radius is tuned for room-scale
     * shading and washes out at the few centimetres where an object actually
     * meets the ground, which is exactly the cue that makes props read as
     * placed rather than floating. This is a ~22 cm horizon term on top.
     */
    float contactAO(vec2 uv, float linZ, vec3 vpos, vec3 vnrm) {
      vec2 radiusUV = uContactRadius * uProjScale / max(linZ, 0.05);
      radiusUV = min(radiusUV, vec2(0.06)); // never smear across the screen up close
      float occ = 0.0;
      for (int i = 0; i < 8; i++) {
        float fi = float(i);
        float a = fi * 0.78539816;
        float ring = mod(fi, 2.0) < 0.5 ? 1.0 : 0.55;
        vec2 suv = uv + vec2(cos(a), sin(a)) * radiusUV * ring;
        float sd = readDepth(clamp(suv, 0.0, 1.0));
        if (sd >= 0.99999) continue;
        vec3 spos = viewPos(suv, linearDepth(sd));
        vec3 diff = spos - vpos;
        float dist = length(diff);
        if (dist < 1e-4) continue;
        float ndl = max(dot(vnrm, diff / dist), 0.0);
        // Falloff so distant geometry behind the sample can't self-occlude.
        occ += ndl * clamp(1.0 - dist / uContactRadius, 0.0, 1.0);
      }
      return clamp(1.0 - (occ / 8.0) * uContactStrength * 2.2, 0.0, 1.0);
    }

    void main() {
      vec3 col = texture2D(tDiffuse, vUv).rgb;
      float d = readDepth(vUv);
      bool isSky = d >= 0.99999;

      // View ray for this pixel, in world space.
      vec4 clip = vec4(vUv * 2.0 - 1.0, -1.0, 1.0);
      vec4 vpos = uInvProjection * clip;
      vec3 vdir = normalize(vpos.xyz / vpos.w);
      vec3 wdir = normalize((uCamWorld * vec4(vdir, 0.0)).xyz);

      float sunAmt = max(dot(wdir, uSunDir), 0.0);
      vec3 fogCol = mix(uFogColor, uFogSunColor, pow(sunAmt, 3.5) * 0.9);
      // Ground-facing rays see less inscatter than horizon-facing ones.
      fogCol *= mix(0.55, 1.0, smoothstep(-0.45, 0.05, wdir.y));

      if (!isSky) {
        float linZ = linearDepth(d);
        float dist = linZ / max(-vdir.z, 1e-4);

        // Contact occlusion first: it is a surface term, so it must land before
        // the fog is mixed in or haze would be darkened along with the surface.
        if (uContactStrength > 0.001 && linZ < 60.0) {
          vec3 vnrm = normalize(texture2D(tNormal, vUv).xyz * 2.0 - 1.0);
          float ao = contactAO(vUv, linZ, viewPos(vUv, linZ), vnrm);
          // Fade out with distance - past ~40 m the kernel is sub-pixel anyway.
          col *= mix(1.0, ao, 1.0 - smoothstep(25.0, 55.0, linZ));
        }

        vec3 wpos = uCamPos + wdir * dist;
        float H = uFogHeight;
        float y0 = uCamPos.y - uFogBase;
        float y1 = wpos.y - uFogBase;
        float dy = y1 - y0;
        float od;
        if (abs(dy) < 0.02) {
          od = uFogDensity * dist * exp(-y0 / H);
        } else {
          od = uFogDensity * dist * (H / dy) * (exp(-y0 / H) - exp(-y1 / H));
        }
        float fog = 1.0 - exp(-max(od, 0.0));
        fog = min(fog, uFogMax);
        col = mix(col, fogCol, fog);
      }

      // Sun shafts: march toward the sun on screen, accumulating unoccluded sky.
      // Gated on world-space alignment as well as screen distance so the shafts
      // stay a tight halo around the sun instead of a grey blob over the block.
      if (uSunScreen.z > 0.5) {
        vec2 toSun = uSunScreen.xy - vUv;
        vec2 aspectDelta = vec2(toSun.x * uAspect, toSun.y);
        float screenDist = length(aspectDelta);
        float reach = exp(-screenDist * 5.5) * uRaySpread * pow(sunAmt, 2.0);
        if (reach > 0.002) {
          vec2 delta = toSun / float(RAY_STEPS) * 0.92;
          vec2 p = vUv + delta * 0.5;
          float illum = 1.0;
          float acc = 0.0;
          for (int i = 0; i < RAY_STEPS; i++) {
            float sd = readDepth(clamp(p, 0.0, 1.0));
            acc += step(0.99999, sd) * illum;
            illum *= uRayDecay;
            p += delta;
          }
          acc /= float(RAY_STEPS);
          col += uSunColor * acc * reach * uRayStrength;
        }
      }

      // Ceiling on the radiance handed to the bloom pass, which is the next
      // thing to run.
      //
      // This is the root of the flickering black square, and guarding the
      // grade pass only fixed half of it. The composer target is HALF FLOAT,
      // so anything above 65504 becomes +Inf, and the bloom blur then turns
      // Inf into NaN the moment it computes Inf*0 or Inf-Inf. That NaN lands
      // in a 1/8-resolution mip, so it covers a large hard-edged block, and
      // downstream clamp() collapses it to pure black.
      //
      // Additive, untone-mapped effects are what get up there: muzzle flashes
      // (yours and the bots'), tracers and blast lights all emit scene-referred
      // radiance with no upper bound.
      //
      // The ceiling is set high on purpose. A blur cannot exceed its brightest
      // input, so anything below the half-float maximum is enough to stop Inf
      // being created — the only requirement is headroom for the bloom sum
      // afterwards. 4096 is ~1500x the bloom threshold of 2.6 and 16x below the
      // half-float ceiling. A first attempt at 64 measurably dimmed the sun
      // (a full-frame diff of 1.86 against a grain noise floor of 1.19 on the
      // into-sun capture), which is real signal being clipped, not safety.
      // Non-finite values are caught too: col == col is false only for NaN.
      if (!(col == col)) col = vec3(0.0);
      gl_FragColor = vec4(min(col, vec3(4096.0)), 1.0);
    }
  `,
};

/**
 * Final grade. Runs AFTER OutputPass + SMAA, so it operates on display-referred
 * sRGB in 0..1 - which is where lens and sensor artefacts actually live. The old
 * stack graded scene-linear HDR, so grain of 0.035 landed on a road whose linear
 * value was 0.01 and buried it in noise, and "contrast around 0.5" pivoted about
 * a value that meant nothing.
 */
const GradeShader = {
  uniforms: {
    tDiffuse: { value: null },
    tBloom: { value: null },
    uTime: { value: 0 },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uVignette: { value: 0.26 },
    uChroma: { value: 0.00034 },
    uGrain: { value: 0.018 },
    uSharpen: { value: 0.13 },
    uDamage: { value: 0.0 },
    uSaturation: { value: 1.12 },
    uContrast: { value: 1.10 },
    uPivot: { value: 0.38 },
    uShadowTint: { value: new THREE.Vector3(0.004, 0.012, 0.030) },
    uHighTint: { value: new THREE.Vector3(0.018, 0.006, -0.014) },
    uDirt: { value: 0.5 },
    uFlash: { value: 0.0 },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
  `,
  fragmentShader: /* glsl */`
    precision highp float;
    uniform sampler2D tDiffuse, tBloom;
    uniform float uTime, uVignette, uChroma, uGrain, uSharpen, uDamage;
    uniform float uSaturation, uContrast, uPivot, uDirt, uFlash;
    uniform vec2 uResolution;
    uniform vec3 uShadowTint, uHighTint;
    varying vec2 vUv;

    float hash(vec2 p) {
      p = fract(p * vec2(443.897, 441.423));
      p += dot(p, p.yx + 19.19);
      return fract((p.x + p.y) * p.x);
    }

    float vnoise(vec2 p) {
      vec2 i = floor(p), f = fract(p);
      f = f * f * (3.0 - 2.0 * f);
      return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x),
                 mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
    }

    // Smeared radial grime on the front element. Only ever visible where the
    // bloom is already strong, which is exactly how real lens dirt behaves.
    float lensDirt(vec2 uv) {
      vec2 p = uv * vec2(uResolution.x / uResolution.y, 1.0);
      float n = vnoise(p * 7.0) * 0.55 + vnoise(p * 17.0) * 0.3 + vnoise(p * 41.0) * 0.15;
      float streak = vnoise(vec2(p.x * 2.0, p.y * 26.0)) * 0.5;
      return clamp((n * 0.8 + streak * 0.4 - 0.34) * 2.6, 0.0, 1.0);
    }

    void main() {
      vec2 uv = vUv;
      vec2 c = uv - 0.5;
      float r2 = dot(c, c);

      // Chromatic aberration grows toward the edges like a real lens.
      float ca = uChroma * (1.0 + r2 * 3.0);
      vec3 col;
      col.r = texture2D(tDiffuse, uv + c * ca).r;
      col.g = texture2D(tDiffuse, uv).g;
      col.b = texture2D(tDiffuse, uv - c * ca).b;
      // Kept for the non-finite fallback at the end of the shader.
      vec3 scene = col;

      // Unsharp mask to restore micro-contrast lost to SMAA. Luminance only -
      // sharpening the channels independently re-amplifies the CA split and
      // turns high-frequency detail (the ground normal map) into RGB confetti.
      vec2 px = 1.0 / uResolution;
      vec3 blur = (
        texture2D(tDiffuse, uv + vec2(px.x, 0.0)).rgb +
        texture2D(tDiffuse, uv - vec2(px.x, 0.0)).rgb +
        texture2D(tDiffuse, uv + vec2(0.0, px.y)).rgb +
        texture2D(tDiffuse, uv - vec2(0.0, px.y)).rgb) * 0.25;
      const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);
      float l0 = dot(col, LUMA);
      float lb = dot(blur, LUMA);
      col *= (l0 + (l0 - lb) * uSharpen) / max(l0, 1e-4);

      // Lens dirt, driven by the bloom buffer so it only lights up on flares.
      //
      // THE BLACK SQUARE LIVED HERE. tBloom is bloom.renderTargetsVertical[2],
      // a 1/8-resolution mip, and the composer runs at half float. A bright
      // enough emissive — a lamp, a muzzle flash — overflows that chain and
      // leaves an Inf or NaN in the mip. One bad texel at 1/8 resolution is a
      // large, hard-edged block once it is sampled at full resolution, and a
      // NaN propagates through the rest of the grade into clamp(col,0,1),
      // where max(NaN, 0.0) resolves to 0.0 on essentially every driver:
      // pure black. That is the flickering square, and it is why it appeared
      // wherever something bright was on screen.
      //
      // So the sample is made finite before it is trusted. Guarding the scalar
      // rather than the vector keeps this valid GLSL ES 1.00 — mix() with a
      // bvec selector is GLSL ES 3.00 only, and this shader is 1.00.
      if (uDirt > 0.001) {
        vec3 b = texture2D(tBloom, uv).rgb;
        float e = dot(b, vec3(0.2126, 0.7152, 0.0722)) * 1.4;
        // e == e is false only for NaN. Inf is handled by the clamp.
        e = (e == e) ? clamp(e, 0.0, 3.0) : 0.0;
        col += lensDirt(uv) * e * uDirt * vec3(1.0, 0.93, 0.82);
      }

      // Filmic S: contrast about a shadow-weighted pivot, then split tone.
      col = (col - uPivot) * uContrast + uPivot;
      float luma = dot(col, vec3(0.2126, 0.7152, 0.0722));
      float sh = 1.0 - smoothstep(0.0, 0.45, luma);
      float hi = smoothstep(0.45, 1.0, luma);
      col += uShadowTint * sh + uHighTint * hi;
      col = mix(vec3(luma), col, uSaturation);

      // Damage: desaturate and push red into the periphery.
      if (uDamage > 0.001) {
        float edge = smoothstep(0.05, 0.55, r2);
        col = mix(col, vec3(luma), uDamage * 0.55);
        col = mix(col, vec3(0.55, 0.03, 0.02), edge * uDamage * 0.85);
      }

      col += uFlash;

      // Vignette - optical falloff, not a black frame.
      col *= 1.0 - uVignette * smoothstep(0.10, 0.78, r2);

      // Animated grain, strongest in the mids/shadows like real sensor noise.
      float n = hash(uv * uResolution + fract(uTime) * 137.0) - 0.5;
      col += n * uGrain * (1.15 - luma * 0.85);

      // Final guard. clamp() is not a safe place for a non-finite value to
      // arrive: min(max(NaN, 0.0), 1.0) collapses to 0.0, so any single NaN
      // anywhere upstream paints a black pixel — and via the low-resolution
      // bloom mip above, a black BLOCK. Falling back to the ungraded scene
      // colour degrades to a slightly flat pixel instead of a hole.
      // col == col compares all components and is false if any is NaN.
      if (!(col == col)) col = scene;
      gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
    }
  `,
};

export class PostFX {
  constructor(engine, { quality = 'high' } = {}) {
    this.engine = engine;
    const { renderer, scene, camera } = engine;
    const { width, height } = engine.size;

    this.composer = new EffectComposer(renderer);
    this.composer.setSize(width, height);

    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    if (quality !== 'low') {
      this.gtao = new GTAOPass(scene, camera, width, height);
      this.gtao.output = GTAOPass.OUTPUT.Default;
      this.gtao.updateGtaoMaterial({
        radius: 0.58,
        distanceExponent: 1.6,
        thickness: 0.35,
        scale: 1.9,
        samples: quality === 'ultra' ? 24 : 16,
        distanceFallOff: 0.9,
        screenSpaceRadius: false,
      });
      this.gtao.updatePdMaterial({ lumaPhi: 8, depthPhi: 2.5, normalPhi: 3.5, radius: 6, samples: 12 });
      this.gtao.blendIntensity = 1.0;
      this._excludeBillboardsFromGTAO(this.gtao);
      this.composer.addPass(this.gtao);

      this.aerial = new ShaderPass(AerialShader);
      this.aerial.uniforms.tDepth.value = this.gtao.depthTexture;
      this.aerial.uniforms.tNormal.value = this.gtao.normalTexture;
      this.aerial.uniforms.uAspect.value = width / height;
      this.composer.addPass(this.aerial);
    }

    // Threshold is in linear radiance. Tone mapping at exposure ~0.55 clips
    // around 3.8, so 1.5 means "the top ~2 stops only" - sky and lit plaster
    // stay out of it, the sun disc, glass hits and muzzle flashes get it.
    this.bloom = new UnrealBloomPass(new THREE.Vector2(width, height), 0.50, 0.85, 2.6);
    this.composer.addPass(this.bloom);

    this.output = new OutputPass();
    this.composer.addPass(this.output);

    if (quality !== 'low') {
      this.smaa = new SMAAPass(width * renderer.getPixelRatio(), height * renderer.getPixelRatio());
      this.composer.addPass(this.smaa);
    }

    // Grade last: display-referred, post-AA.
    this.grade = new ShaderPass(GradeShader);
    this.grade.uniforms.uResolution.value.set(width, height);
    this.grade.uniforms.tBloom.value = this.bloom.renderTargetsVertical[2].texture;
    this.composer.addPass(this.grade);

    this._sunWorld = new THREE.Vector3();
    this._sunProj = new THREE.Vector3();
  }

  setDamage(v) { this.grade.uniforms.uDamage.value = v; }
  setFlash(v) { this.grade.uniforms.uFlash.value = v; }

  /**
   * Keep transparent billboards out of GTAO's depth/normal G-buffer.
   *
   * This is the flickering black square. GTAOPass builds its G-buffer by
   * setting `scene.overrideMaterial` to an opaque MeshNormalMaterial and
   * rendering the whole scene — and `overrideVisibility()` only hides Points
   * and Lines. An override material replaces the real one, so `transparent:
   * true` and `depthWrite: false` stop meaning anything: every effect
   * billboard is written into the G-buffer as if it were solid geometry.
   *
   * The world muzzle-flash card is a 1x1 QUAD sitting centimetres in front of
   * the eye. GTAO therefore sees a solid wall pressed against the camera
   * covering a large square of the screen, computes full occlusion there, and
   * multiplies the frame to pure black in exactly that quad's shape — for the
   * ~50 ms the flash is alive. Captured evidence: triangle count, draw calls
   * and shader-program count all unchanged on the bad frame, so nothing went
   * missing and nothing recompiled; something was drawn.
   *
   * A billboard has no business occluding anything in ambient occlusion in the
   * first place, so hiding these is also what is physically correct.
   */
  _excludeBillboardsFromGTAO(gtao) {
    const isBillboard = (o) => o.isMesh && o.material
      && (o.userData.noGTAO === true
        || (o.material.transparent === true && o.material.depthWrite === false));

    const origOverride = gtao.overrideVisibility.bind(gtao);
    gtao.overrideVisibility = () => {
      // The base call caches EVERY object's visibility first, so anything
      // hidden afterwards is still restored correctly by restoreVisibility().
      origOverride();
      gtao.scene.traverse((o) => { if (isBillboard(o)) o.visible = false; });
    };
  }

  resize(width, height) {
    this.composer.setSize(width, height);
    this.grade.uniforms.uResolution.value.set(width, height);
    this.bloom?.setSize(width, height);
    this.gtao?.setSize(width, height);
    if (this.aerial) {
      this.aerial.uniforms.uAspect.value = width / height;
      this.aerial.uniforms.tDepth.value = this.gtao.depthTexture;
      this.aerial.uniforms.tNormal.value = this.gtao.normalTexture;
    }
    this.smaa?.setSize(
      width * this.engine.renderer.getPixelRatio(),
      height * this.engine.renderer.getPixelRatio()
    );
  }

  _updateAerial() {
    const atmo = this.engine.atmosphere;
    if (!this.aerial || !atmo) return;
    const cam = this.engine.camera;
    const u = this.aerial.uniforms;

    u.uInvProjection.value.copy(cam.projectionMatrixInverse);
    u.uCamWorld.value.copy(cam.matrixWorld);
    u.uCamPos.value.setFromMatrixPosition(cam.matrixWorld);
    u.uNear.value = cam.near;
    u.uFar.value = cam.far;
    // Half the projection scale: world metres -> uv at unit view depth.
    u.uProjScale.value.set(cam.projectionMatrix.elements[0] * 0.5,
                           cam.projectionMatrix.elements[5] * 0.5);
    u.uSunDir.value.copy(atmo.sunDir);

    // Sun on screen, for the shaft march.
    this._sunWorld.copy(u.uCamPos.value).addScaledVector(atmo.sunDir, 4000);
    this._sunProj.copy(this._sunWorld).project(cam);
    const inFront = this._sunProj.z < 1;
    u.uSunScreen.value.set(
      this._sunProj.x * 0.5 + 0.5,
      this._sunProj.y * 0.5 + 0.5,
      inFront ? 1 : 0
    );
  }

  render(dt, elapsed) {
    // The aerial pass samples GTAO's depth prepass; without it the depth is
    // stale and the fog ghosts last frame's geometry.
    if (this.aerial && this.gtao) this.aerial.enabled = this.gtao.enabled;
    this.grade.uniforms.uTime.value = elapsed;
    this._updateAerial();
    this.composer.render(dt);
  }
}
